// declare function require(name:string);
declare global {
    interface Window {
        $: any;
        jQuery: any;
        s: { sessionLifetime?: number; [key: string]: any };
        v: any;
        l: any;
        c: any;
        clearClientSession: () => void;
    }
    interface ArrayConstructor {
        from(arrayLike: any, mapFn?, thisArg?): Array<any>;
    }
}

import axiosOriginal from 'axios';
import Cookies from 'js-cookie';
import Swal from 'lib/SwalExtension';
import { __ } from 'lib/Utilities';
import {Promise, polyfill as promisePolyfill } from 'es6-promise';

promisePolyfill();

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

axiosOriginal.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/**
 * Next we will register the CSRF Token as a common header with Axios so that
 * all outgoing HTTP requests automatically have it attached. This is just
 * a simple convenience so we don't have to attach every token manually.
 */

const token =  document.querySelector('meta[name="csrf-token"]');

if (token) {
    axiosOriginal.defaults.headers.common['X-CSRF-TOKEN'] = token.getAttribute("content");
} else {
    console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token');
}

/**
 * A browser stores the setTimeout delay in a signed 32-bit integer, so any delay above 2^31-1 ms (~24.9 days) wraps around. A site whose OverrideSessionTimeout customization is large enough - 262800 minutes, for example - produced a negative delay, which the browser clamps to 0, firing the "session expired" dialog on page load and logging the user straight out. Re-arm in chunks so lifetimes beyond the ceiling are still honoured.
 */
const MAX_TIMEOUT_MS = 2147483647; // 2^31 - 1

function scheduleAfter(delayMs: number, callback: () => void): void {
    if (delayMs > MAX_TIMEOUT_MS) {
        window.setTimeout(() => scheduleAfter(delayMs - MAX_TIMEOUT_MS, callback), MAX_TIMEOUT_MS);

        return;
    }

    window.setTimeout(callback, Math.max(0, delayMs));
}

/**
 * Wipe the client-side traces of the signed-in session (PE-1032 AC #4).
 *
 * Shared with the logout links in mobile-menu.blade.php and
 * user-widget.blade.php (via window.clearClientSession below), so a timed-out
 * session leaves the same state behind as a deliberate sign-out.
 *
 * venngo_cookies is a shared bag - an in-flight SMB purchase keeps its wizard
 * state under `smbCookies` in the same cookie (see Purchase.ts) - so only the
 * locationState key is dropped, not the whole cookie.
 */
function clearClientSession(): void {
    try {
        const rawCookie = Cookies.get('venngo_cookies');

        if (rawCookie) {
            const venngoCookies = JSON.parse(rawCookie);

            if (venngoCookies && typeof venngoCookies === 'object' && 'locationState' in venngoCookies) {
                delete venngoCookies.locationState;

                if (Object.keys(venngoCookies).length === 0) {
                    Cookies.remove('venngo_cookies', { path: '/' });
                } else {
                    // Rewritten without `expires` on purpose. js-cookie cannot read
                    // the current expiry back, and every key left in the bag is
                    // shorter-lived than the 365 days locationState was written
                    // with: Purchase.ts writes smbCookies/userId with
                    // { expires: 1/24 }, User.ts writes no expiry at all. Stamping
                    // 365 days here would promote in-flight billing details to a
                    // year-long cookie, the opposite of what this ticket asks for.
                    Cookies.set('venngo_cookies', JSON.stringify(venngoCookies), {
                        path: '/',
                    });
                }
            }
        }
    } catch (e) {
        console.error('Unable to clear locationState from venngo_cookies', e);
    }

    try {
        // Throws outright where the browser blocks site data (Safari private
        // mode, "block all cookies"), which must not stop the logout below.
        window.localStorage.clear();
    } catch (e) {
        console.error('Unable to clear local storage', e);
    }
}

// The onclick handlers in mobile-menu.blade.php and user-widget.blade.php call
// this, so the manual-logout and session-expiry paths cannot drift apart.
window.clearClientSession = clearClientSession;

/**
 * Both expiry paths - the scheduled timeout and a 401 from the server - clear
 * first and prompt second: the session is already gone, and the user may never
 * press the confirm button (or may just close the tab), which would otherwise
 * leave their location and filters behind.
 */
function showSessionExpiredDialog(): void {
    clearClientSession();

    Swal.fire({
        title: __('session_expired.title'),
        text: __('session_expired.text'),
        icon: "error",
        confirmButtonText: __('session_expired.confirmButtonText'),
    }).then(() => {
        const logoutForm = <HTMLFormElement>document.getElementById('logout-form');

        if (logoutForm) {
            logoutForm.submit();
        }
    });
}

const sessionLifetimeMinutes: number = Number(window.s?.sessionLifetime) > 0
    ? Number(window.s.sessionLifetime)
    : 120;

scheduleAfter(sessionLifetimeMinutes * 60 * 1000, showSessionExpiredDialog);

axiosOriginal.interceptors.response.use((response) => {
    return response;
}, (error) => {
    if (error.response && error.response.status === 401) {
        showSessionExpiredDialog();
    }
    return Promise.reject(error);
});

export const axios = axiosOriginal;
